import { useEffect, useMemo, useState } from "react"
import {
  Flex,
  HStack,
  Separator,
  Spacer,
  Stack,
} from "@chakra-ui/react"
import { GrantBox } from "./GrantBox"
import { Button, Text, DollarInput } from "@/components/ui"
import { SCHEDULE_CLOSING_MEETING_STEP_INDEX } from "@/constants"
import { DealOffer, DealOfferGrant } from "@/types"
import { format } from "@/utils"
import { DecisionOfferModal } from "."

interface Props {
  dealId: number
  offer: DealOffer
  currentStepIndex: number
}

function calculateLp(offer: DealOffer): number {
  const { allocationAgreement, ppSplit, isPreferentialReturn, desiredLiquidity, offerGrants, pp } = offer
  const optioneePp = 1 - (pp ?? 0)
  if (optioneePp === 0) return 0

  const includedGrants = (offerGrants ?? []).filter((og) => og.included === 1)
  const initialPayment = includedGrants.reduce(
    (sum, og) => sum + og.grant.vested * og.grant.price + (og.grant.tax ?? 0), 0
  )
  const additionalPayment = desiredLiquidity ?? 0
  const totalUpfrontPayment = initialPayment + additionalPayment

  const subjectToPp = totalUpfrontPayment * ppSplit
  const subjectToLp2x = totalUpfrontPayment - subjectToPp
  const lpBeforeDeferredTaxPremium = subjectToPp * optioneePp + 2 * subjectToLp2x

  if (allocationAgreement === "AA") {
    const esoPpAdjustmentFactor = (1 - 0.2) / (1 - 0.37)
    return lpBeforeDeferredTaxPremium * esoPpAdjustmentFactor - totalUpfrontPayment * (esoPpAdjustmentFactor - 1)
  }

  if (allocationAgreement === "RSU" && isPreferentialReturn === 1) {
    return lpBeforeDeferredTaxPremium
  } else {
    return Math.max(lpBeforeDeferredTaxPremium, totalUpfrontPayment * optioneePp)
  }
}

const OfferBox = ({ label, description, value, bg, color }: { label: string, description?: string, value: string, bg?: string, color?: string }) => (
  <Stack w="50%" p={5} borderRadius={10} bg={bg ?? "white"} boxShadow="md">
    <Text fontSize="sm" color={color ?? "black"}>{label}</Text>
    {description && <Text fontSize="xs" color={color ?? "gray.500"}>{description}</Text>}
    <Text fontSize="2xl" fontWeight="bold" color={color ?? "black"}>{value}</Text>
  </Stack>
)

export const OfferComponent = ({ dealId, offer, currentStepIndex }: Props) => {
  const [hypotheticalSharePrice, setHypotheticalSharePrice] = useState<number>(0)
  const [offerState, setOfferState] = useState<"pending" | "accepted" | "declined">("pending")
  const [isAcceptOfferModalOpen, setIsAcceptOfferModalOpen] = useState<boolean>(false)

  const includedGrants = useMemo(() =>
    (offer.offerGrants ?? []).filter((og) => og.included === 1),
    [offer.offerGrants]
  )

  const exerciseCost = useMemo(() =>
    includedGrants.reduce((sum, og) => sum + og.grant.vested * og.grant.price + (og.grant.tax ?? 0), 0),
    [includedGrants]
  )

  const lp = useMemo(() => calculateLp(offer), [offer])

  const { grossProceeds, netFinalDistribution, netProceeds } = useMemo(() => {
    const totalShares = includedGrants.reduce((sum, og) => sum + og.grant.vested, 0)
    const grossProceeds = totalShares * hypotheticalSharePrice
    const esoParticipation = grossProceeds * offer.pp
    const distribution = grossProceeds - esoParticipation - lp
    const netFinalDistribution = distribution > 0 ? distribution : 0

    let nsoTax = 0
    for (const og of includedGrants) {
      if (og.grant.grantType === "nso") {
        nsoTax += og.grant.tax ?? 0
      }
    }

    const netProceeds = (offer.desiredLiquidity ?? 0) + netFinalDistribution + nsoTax
    return { grossProceeds, netFinalDistribution, netProceeds }
  }, [hypotheticalSharePrice, offer, includedGrants, lp])

  useEffect(() => {
    setHypotheticalSharePrice(offer.hypotheticalSharePrice ?? 0)
  }, [offer])

  const handleHypotheticalSharePriceChange = (value: string | number) => {
    const num = Number(value);
    if (!isNaN(num)) {
      setHypotheticalSharePrice(num);
    } else {
      setHypotheticalSharePrice(0);
    }
  }

  const handleAcceptOffer = () => {
    setOfferState("accepted")
    setIsAcceptOfferModalOpen(true)
  }

  const handleDeclineOffer = () => {
    setOfferState("declined")
    setIsAcceptOfferModalOpen(true)
  }

  return (
    <Stack w="100%" gap={5} mb={10} pl={10} pr={10} pb={20}>
      {
        currentStepIndex >= SCHEDULE_CLOSING_MEETING_STEP_INDEX && offer.isAccepted && (
          <Text fontSize="lg" fontWeight="bold" color="green.600" mt={5}>
            You have accepted this offer.
          </Text>
        )
      }
      <HStack w="100%" justifyContent="space-between" mt={currentStepIndex >= SCHEDULE_CLOSING_MEETING_STEP_INDEX && offer.isAccepted ? 0 : 10}>
        <Stack w="50%" p={5} borderRadius={10} bg="gray.50" boxShadow="sm">
          <Text fontSize="xl" fontWeight="bold" pb={5}>Your Ask</Text>
          <HStack w="100%">
            <OfferBox label="Desired Liquidity" value={format((offer.desiredLiquidity ?? 0).toLocaleString())} bg="brand.100" color="white" />
            <OfferBox label="Exercise Cost" value={format(exerciseCost.toLocaleString())} />
          </HStack>
        </Stack>

        <Stack w="50%" p={5} borderRadius={10} bg="gray.50" boxShadow="sm">
          <Text fontSize="xl" fontWeight="bold" pb={5}>ESO Offer Details</Text>
          <HStack w="100%">
            <OfferBox label="Participation Percentage" value={`${(offer.pp * 100).toLocaleString()}%`} />
            <OfferBox label="Liquidation Preference" value={format(lp.toLocaleString())} />
          </HStack>
        </Stack>
      </HStack>

      <Stack pt={5} pb={5}>
        <Text fontSize="sm" fontWeight="bold" pb={5}>What's Covered</Text>
        <HStack gap={6} overflowX="scroll" p={1}>
          {
            includedGrants.map((og) => (
              <GrantBox key={og.id} grant={og.grant} />
            ))
          }
        </HStack>
      </Stack>
      <HStack mt={5} w="100%">
        <Spacer />
        {currentStepIndex < SCHEDULE_CLOSING_MEETING_STEP_INDEX && (
          <>
            <Button
              variant="solid"
              disabled={offer.isAccepted}
              onClick={handleAcceptOffer}
            >
              {offer.isAccepted ? "Accepted" : "Accept Offer"}
            </Button>

            <Button variant="outline" onClick={handleDeclineOffer}>
              Decline Offer
            </Button>
          </>
        )}
      </HStack>
      <Separator />
      <Text fontSize="xl" fontWeight="bold" mt={5}>Offer Distribution Details</Text>
      <Flex w="100%" mb={5}>
        <Stack w="25%" h="fit-content" p={5} borderRadius={10} bg="gray.50" boxShadow="sm">
          <Text fontSize="sm" pb={5}>Enter a share price to see how much you would make</Text>
          <Text fontSize="sm" fontWeight="bold">Hypothetical Share Price</Text>
          <DollarInput value={hypotheticalSharePrice} setValue={handleHypotheticalSharePriceChange} />
        </Stack>

        <Stack w="75%" pl={5}>
          <HStack w="100%">
            <OfferBox
              label="Gross Proceeds"
              description={"(Total Shares x Hypothetical Share Price)"}
              value={`$${grossProceeds.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`}
            />
            <OfferBox
              label="ESO Proceeds"
              description={`(${(offer.pp * 100).toFixed(2)}% x Gross Proceeds + Liquidation Preference)`}
              value={`$${(grossProceeds * offer.pp + lp).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`}
            />
          </HStack>
          <HStack w="100%">
            <OfferBox
              label="Your Net Final Distribution"
              description={"(Gross Proceeds - ESO Proceeds)"}
              value={`$${netFinalDistribution.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`}
            />
            <OfferBox
              label="Your Total Payout"
              description={"(Desired Liquidity + Your Net Final Distribution)"}
              value={`$${netProceeds.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`}
              bg="brand.100"
              color="white"
            />
          </HStack>
        </Stack>
      </Flex>

      <DecisionOfferModal
        isAccept={offerState === "accepted"}
        dealId={dealId}
        offerNumber={offer.offerNumber}
        isOpen={isAcceptOfferModalOpen}
        onClose={() => setIsAcceptOfferModalOpen(false)}
      />
    </Stack>
  )
}
